import { isEmpty } from 'lodash';
import { PropsWithChildren, useEffect, useMemo, useState } from 'react';

import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { useReportError } from '@/hooks/useReportError';
import { groupSuggestionsByEntityOrTestamentaryEntityID } from '@/modules/aiSuggestions/aiSuggestions.utils';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { EntityStage } from '@/types/schema';
import { getNodes } from '@/utils/graphqlUtils';

import { useEstatePageDataQuery } from '../graphql/EstatePageData.generated';
import { getEstatePageSuggestionWhere } from '../graphql/EstatePageData.utils';
import { EstateHoldingsContext } from './estateHoldings.context';

export function EstateHoldingsProvider({ children }: PropsWithChildren) {
  const { showFeedback } = useFeedback();
  const { reportError } = useReportError();
  const { primaryClients, householdId } = useHouseholdDetailsContext();

  const [loading, setLoading] = useState(true);
  const [entities, setEntities] =
    useState<EstateHoldingsContext['entities']>(null);
  const [testamentaryEntities, setTestamentaryEntities] =
    useState<EstateHoldingsContext['testamentaryEntities']>(null);
  const [draftEntities, setDraftEntities] =
    useState<EstateHoldingsContext['draftEntities']>(null);
  const [unacknowledgedSuggestionsV2, setUnacknowledgedSuggestionsV2] =
    useState<EstateHoldingsContext['unacknowledgedSuggestionsV2']>(
      {} as EstateHoldingsContext['unacknowledgedSuggestionsV2']
    );
  const [error, setError] = useState<EstateHoldingsContext['error']>(null);
  const [refetch, setRefetch] =
    useState<EstateHoldingsContext['refetch']>(null);

  const {
    data,
    loading: queryLoading,
    error: queryError,
    refetch: queryRefetch,
  } = useEstatePageDataQuery({
    skip: isEmpty(primaryClients) || !householdId,
    notifyOnNetworkStatusChange: true,
    variables: {
      ...getEstatePageSuggestionWhere(householdId!, {
        holdingTargetWhere: {
          hasEntityWith: [{ hasHouseholdWith: [{ id: householdId }] }],
        },
      }),
      entitiesLike: {
        stageIn: [EntityStage.Active, EntityStage.PreCreated], // Exclude draft & completed entities from main holdings
        hasHouseholdWith: [
          {
            id: householdId,
          },
        ],
      },
      householdId: householdId!,
      includeDraftEntities: true,
      includeTestamentaryEntities: true,
    },
    errorPolicy: 'all',
    fetchPolicy: 'cache-and-network', // Use cache first for consistent ordering, then fetch fresh data
    onError: (err) => {
      reportError('failed to load direct holdings', err, {
        householdId,
      });
      showFeedback(
        "We weren't able to load direct holdings. Please refresh the page and try again."
      );
      setError(err);
    },
  });

  // Update loading state
  useEffect(() => {
    setLoading(queryLoading);
  }, [queryLoading]);

  // Update error state
  useEffect(() => {
    setError(queryError || null);
  }, [queryError]);

  // Update refetch function
  useEffect(() => {
    setRefetch(() => queryRefetch);
  }, [queryRefetch]);

  // Update holdings when data changes
  useEffect(() => {
    if (data) {
      const entities = getNodes(data.entities);
      const testamentaryEntitiesNodes = getNodes(data.testamentaryEntities);
      const draftEntitiesNodes = getNodes(data.draftEntities);
      const allUnacknowledgedSuggestions =
        groupSuggestionsByEntityOrTestamentaryEntityID([
          ...getNodes(data.aiSuggestions),
          ...getNodes(data.holdingSuggestions),
        ]);

      setEntities(entities);
      setTestamentaryEntities(testamentaryEntitiesNodes);
      setDraftEntities(draftEntitiesNodes);
      setUnacknowledgedSuggestionsV2(allUnacknowledgedSuggestions);
      setError(null);
    }
  }, [data]);

  const contextValue: EstateHoldingsContext = useMemo(
    () => ({
      loading,
      setLoading,
      entities,
      setEntities,
      testamentaryEntities,
      setTestamentaryEntities,
      draftEntities,
      setDraftEntities,
      unacknowledgedSuggestionsV2,
      setUnacknowledgedSuggestionsV2,
      error,
      setError,
      refetch,
      setRefetch,
    }),
    [
      loading,
      entities,
      testamentaryEntities,
      draftEntities,
      unacknowledgedSuggestionsV2,
      error,
      refetch,
    ]
  );

  return (
    <EstateHoldingsContext.Provider value={contextValue}>
      {children}
    </EstateHoldingsContext.Provider>
  );
}
